import { assetsService } from "../services/assetsService";
import { ScreenData } from "../types/ScreenTypes";

const CONCURRENCY = 3;
const FETCH_TIMEOUT_MS = 15000;

export const NON_IMAGE_TYPES = ["html", "dua", "special", "video", "youtube"];

function collectAssetUrls(screenData: ScreenData): string[] {
  const urls = new Set<string>();

  const backgrounds = [
    screenData.backgroundUrl,
    screenData.countdownBackgroundUrl,
    screenData.blankScreenBackgroundUrl,
    screenData.slideshowBackgroundUrl,
    screenData.salahChangesBackgroundUrl,
    screenData.afterPrayerSalahChangesBackgroundUrl,
  ];
  backgrounds.forEach((url) => url && urls.add(url));

  // The screen endpoint sends flat assetUrl/assetType on items (asset is null);
  // other paths use a nested asset object — accept both
  (screenData.playlists || []).forEach((playlist) => {
    (playlist.items || []).forEach((item: any) => {
      const url = item.asset?.url ?? item.assetUrl;
      const type = item.asset?.type ?? item.assetType;
      if (
        url &&
        type &&
        !url.startsWith("<") &&
        !NON_IMAGE_TYPES.includes(type.toLowerCase())
      ) {
        urls.add(url);
      }
    });
  });

  return Array.from(urls);
}

// Fetching through the service worker's CacheFirst route is what caches the
// asset; cache hits cost nothing, so re-running on every poll is cheap and
// also refreshes the cache expiry.
export async function prefetchScreenAssets(
  screenData: ScreenData,
): Promise<void> {
  const paths = collectAssetUrls(screenData);
  if (paths.length === 0) return;

  const queue = [...paths];
  let failures = 0;

  const worker = async () => {
    for (let path = queue.shift(); path; path = queue.shift()) {
      try {
        const url = await assetsService.getAsset(path, "poster");
        if (!url || !url.includes("/digital-signage/")) continue;
        const response = await fetch(url, {
          mode: "cors",
          signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
        });
        if (!response.ok) failures++;
      } catch {
        failures++;
      }
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, paths.length) }, worker),
  );

  console.log(
    `[assetPrefetcher] Prefetched ${paths.length - failures}/${paths.length} assets`,
  );
}
